import { Checkbox } from "@/components/ui/checkbox";
import { formatTaxRateInput } from "@rebuild/shared";

interface PDFTaxControlsProps {
  estimatePropertyAddress?: string;
  hasAddress: boolean;
  includeTax: boolean;
  /** Current ZipTax suggestion for the estimate address, if one is available. */
  ziptaxRate?: number;
  /** Whether the current rate is a manual override rather than the ZipTax suggestion. */
  isManualRate: boolean;
  /** True while a cached preview's suggestion is still being fetched. */
  isSuggestionLoading: boolean;
  onIncludeTaxChange: (includeTax: boolean) => void;
  onResetToSuggestion: () => void;
}

export function PDFTaxControls({
  estimatePropertyAddress,
  hasAddress,
  includeTax,
  ziptaxRate,
  isManualRate,
  isSuggestionLoading,
  onIncludeTaxChange,
  onResetToSuggestion,
}: PDFTaxControlsProps) {
  const hasSuggestion = ziptaxRate !== undefined;

  const renderContextCopy = () => {
    if (!hasAddress) {
      return (
        <p className="pl-6 text-xs leading-5 text-amber-700 italic">
          Add an address to the project to calculate sales tax.
        </p>
      );
    }

    if (hasSuggestion) {
      // "Apply suggested tax" appears only once the rate has been edited off the suggestion.
      return (
        <p className="pl-6 text-xs leading-5 text-gray-600">
          Tax based on {estimatePropertyAddress} is {formatTaxRateInput(ziptaxRate)}%.
          {isManualRate && (
            <button
              type="button"
              onClick={onResetToSuggestion}
              className="ml-1 cursor-pointer text-xs font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
            >
              Apply suggested tax.
            </button>
          )}
        </p>
      );
    }

    // Cached preview whose suggestion is still being fetched — stay quiet until it resolves.
    if (isSuggestionLoading) {
      return null;
    }

    // Has an address but ZipTax could not return a rate.
    return <p className="pl-6 text-xs leading-5 text-amber-700">Unable to calculate tax. You can still export without tax.</p>;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Checkbox checked={includeTax} onCheckedChange={(checked) => onIncludeTaxChange(checked === true)} />
        <label className="cursor-pointer text-sm text-gray-700" onClick={() => onIncludeTaxChange(!includeTax)}>
          Include Sales Tax
        </label>
      </div>

      {renderContextCopy()}
    </div>
  );
}
